import "./DisplayAlert.module.scss";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Button, Chip, IconButton, Tooltip } from "@mui/material";
import { BulletinResource } from "@rubi/api-generated";
import { PageHeader } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { bulletinEditPath } from "@/components/alerts/add-v2/routePaths";
import { ALERT_STATUS } from "@/components/alerts/constants";

import {
  ArrowsOutIcon,
  ChatCircleIcon,
  IntersectIcon,
  PencilSimpleIcon,
  WarningCircleIcon,
  XIcon,
} from "./HeaderIcons";

// Header actions are neutral (text.primary) per the Figma TopBar, not primary.
const actionSx = { color: "text.primary" } as const;

// Below this panel width the labelled actions collapse to round icon buttons,
// matching the narrow TopBar in Figma. Sized to fit the longer French labels.
// The label is visually hidden rather than removed so it stays the button's
// accessible name.
const COLLAPSE_WIDTH = "640px";
const collapsibleSx = {
  ...actionSx,
  [`@container (max-width: ${COLLAPSE_WIDTH})`]: {
    minWidth: 0,
    p: 1,
    borderRadius: "50%",
    "& .MuiButton-startIcon": {
      m: 0,
      "& > :first-of-type": { fontSize: 24 },
    },
    "& .action-label": {
      position: "absolute",
      width: "1px",
      height: "1px",
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
    },
  },
} as const;

interface DisplayAlertTopButtonProps {
  currentItem: BulletinResource;
  mode: ALERT_STATUS;
  display_type: "main" | "compare";
  searchParams: URLSearchParams;
  setSearchParams: (
    params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
  ) => void;
  readOnly: boolean;
  isCreator: boolean;
  isResolveOpen: boolean;
  setIsResolveOpen: (value: boolean) => void;
  hasUnpublishedResolution: boolean;
  setDrawerWidth: (width: number) => void;
}

export const DisplayAlertTopButton = ({
  currentItem,
  mode,
  display_type,
  searchParams,
  setSearchParams,
  readOnly,
  isCreator,
  isResolveOpen,
  setIsResolveOpen,
  hasUnpublishedResolution,
  setDrawerWidth,
}: DisplayAlertTopButtonProps) => {
  const { t } = useTranslation("bulletin");
  const navigate = useNavigate();

  const isDraft = currentItem?.is_draft === 1;
  const alertsOrDrafts = isDraft ? "drafts" : "alerts";
  const idToUse =
    display_type === "main"
      ? parseInt(searchParams.get("bulletinId") ?? "0")
      : parseInt(searchParams.get("compareBulletinId") ?? "0");

  const isCompare = display_type === "compare";
  const pinToFront = searchParams.get("pinToFront");
  const isShowingSimilar = searchParams.get("similarTo");
  const isPinnedToFront = Boolean(pinToFront);
  const canEdit = readOnly || (!readOnly && isCreator);
  const canResolve = mode !== ALERT_STATUS.DRAFT && !readOnly;
  const canSeeSimilar = readOnly || mode !== ALERT_STATUS.DRAFT;

  // Pinned panel gets a subtle gray tint so users can tell which side is anchored.
  const isPinnedHighlight =
    isCompare || (display_type === "main" && pinToFront === idToUse.toString());

  const onEditAlert = () => {
    navigate(bulletinEditPath(idToUse));
  };

  const closeCompareBulletin = () => {
    setSearchParams((searchParams) => {
      if (searchParams.get("pinToFront")) {
        searchParams.delete("compareBulletinId");
        setDrawerWidth(window.innerWidth * 0.5);
      }
      return searchParams;
    });
  };

  // Dropping bulletinId is what closes the drawer; AlertsList syncs the drawer
  // to the URL. Closing it here as well would desync it if accessGuard blocks
  // the navigation and the user chooses to stay.
  const handleMainClose = () => {
    setSearchParams((searchParams) => {
      const pinToFront = searchParams.get("pinToFront");
      const compareBulletinId = searchParams.get("compareBulletinId");
      if (pinToFront && compareBulletinId) {
        searchParams.set("bulletinId", compareBulletinId);
        searchParams.delete("compareBulletinId");
        setDrawerWidth(window.innerWidth * 0.5);
      } else {
        searchParams.delete("bulletinId");
      }
      searchParams.delete("pinToFront");
      searchParams.delete("similarTo");
      return searchParams;
    });
  };

  const togglePinToFront = () => {
    setSearchParams((searchParams) => {
      if (isPinnedToFront) {
        searchParams.delete("pinToFront");
        searchParams.delete("compareBulletinId");
      } else {
        searchParams.set("pinToFront", "true");
      }
      return searchParams;
    });
  };

  const renderResolveButton = () => {
    if (!canResolve) return null;
    return (
      <Tooltip
        title={
          hasUnpublishedResolution
            ? t("unpublished_resolution")
            : t("click_to_comment")
        }
      >
        <IconButton
          onClick={() => setIsResolveOpen(!isResolveOpen)}
          sx={actionSx}
        >
          {hasUnpublishedResolution ? (
            <WarningCircleIcon color="error" />
          ) : (
            <ChatCircleIcon />
          )}
        </IconButton>
      </Tooltip>
    );
  };

  const renderEditButton = () => {
    if (!canEdit) return null;
    return (
      <Tooltip title={t("edit_bulletin")}>
        <IconButton onClick={onEditAlert} sx={actionSx}>
          <PencilSimpleIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const renderShowSimilar = () => {
    if (!canSeeSimilar) return null;

    // Hide the show-similar control on the compare panel while pinned: the
    // main panel already owns it in that mode.
    if (isCompare && pinToFront) return null;

    if (!isShowingSimilar) {
      return (
        <Tooltip title={t("find_similar")}>
          <Button
            sx={collapsibleSx}
            startIcon={<IntersectIcon />}
            onClick={() => {
              setSearchParams((searchParams) => {
                searchParams.set("similarTo", currentItem?.id.toString());
                searchParams.set("pinToFront", "true");
                return searchParams;
              });
            }}
          >
            <span className="action-label">{t("find_similar")}</span>
          </Button>
        </Tooltip>
      );
    }

    if (isShowingSimilar === currentItem?.id?.toString()) {
      return (
        <Tooltip title={"Hide similar alerts"}>
          <Button
            sx={collapsibleSx}
            startIcon={<XIcon />}
            onClick={() => {
              setSearchParams((searchParams) => {
                searchParams.delete("similarTo");
                return searchParams;
              });
            }}
          >
            <span className="action-label">{t("hide_similar")}</span>
          </Button>
        </Tooltip>
      );
    }

    return (
      <>
        <Chip
          label={`Similar to ${isShowingSimilar}`}
          color="success"
          variant="outlined"
          onClick={() => {
            setSearchParams((searchParams) => {
              if (searchParams.get("similarTo")) {
                searchParams.set("bulletinId", searchParams.get("similarTo")!);
              }
              return searchParams;
            });
          }}
        />
        <Tooltip title={t("find_similar")}>
          <Button
            sx={collapsibleSx}
            startIcon={<IntersectIcon />}
            onClick={() => {
              setSearchParams((searchParams) => {
                searchParams.set("similarTo", currentItem?.id.toString());
                return searchParams;
              });
            }}
          >
            <span className="action-label">{t("find_similar")}</span>
          </Button>
        </Tooltip>
      </>
    );
  };

  const renderOpenInNew = () => (
    <Tooltip title={t("open_in_new_tab", { defaultValue: "Open in new tab" })}>
      <IconButton
        aria-label="open in new tab"
        sx={actionSx}
        onClick={() => {
          window.open(
            `/rubialert/${alertsOrDrafts}/${currentItem?.id}/single`,
            "_blank",
          );
        }}
      >
        <ArrowsOutIcon />
      </IconButton>
    </Tooltip>
  );

  const renderPin = () => {
    // Hide pin on the compare panel while another bulletin is pinned —
    // pinning is a main-panel operation in that mode.
    if (isCompare && pinToFront) return null;
    return (
      <Tooltip
        title={isPinnedToFront ? t("unpin_bulletin") : t("pin_bulletin")}
      >
        {/* Labelled button (icon-only only on narrow panels) so the comparison
            purpose is discoverable; the pinned state swaps to a filled icon +
            outlined border so it reads as "on". */}
        <Button
          aria-pressed={isPinnedToFront}
          color="inherit"
          sx={collapsibleSx}
          variant={isPinnedToFront ? "outlined" : "text"}
          startIcon={
            isPinnedToFront ? <PushPinIcon /> : <PushPinOutlinedIcon />
          }
          onClick={togglePinToFront}
        >
          <span className="action-label">
            {isPinnedToFront ? t("pinned") : t("pin_to_compare")}
          </span>
        </Button>
      </Tooltip>
    );
  };

  const renderClose = () => {
    // Slideout/embedded view → always exit via a right-side X. Each panel
    // closes itself: compare → drops compareBulletinId; main → closes the
    // drawer (or promotes the compare bulletin when one is pinned).
    return (
      <Tooltip title={t("close_bulletin", { defaultValue: "Close" })}>
        <IconButton
          onClick={isCompare ? closeCompareBulletin : handleMainClose}
          aria-label={isCompare ? "close compare" : "close bulletin"}
          sx={actionSx}
        >
          <XIcon />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <PageHeader
      highlighted={isPinnedHighlight}
      right={
        <>
          {renderResolveButton()}
          {renderEditButton()}
          {renderShowSimilar()}
          {renderPin()}
          {renderOpenInNew()}
          {renderClose()}
        </>
      }
    />
  );
};
